import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { EvAccordionComponent } from "ev-accordion";
import {
    CheckBoxAlternative,
    EvMultiselectDropdownComponentV2,
} from "src/app/shared-components/ev-multiselect-dropdown-v2/ev-multiselect-dropdown-v2.component";
import { RightSidebarService, RightSidebarOptions } from "src/services/right-sidebar.service";
import { CommercialFilterStore } from "src/stores/commercial-filter.store";
import { rxResource, takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { Observable, Subject, map } from "rxjs";
import { deepDistinctUntilChanged } from "src/util/tools/compare.util";
import { MapDataService } from "src/services/map/map-data.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { TimePeriodComponent } from "./components/time-period/time-period.component";
import { EVEstateType } from "src/models/units/unit-attributes.model";

@Component({
    selector: "commercial-filter",
    imports: [EvMultiselectDropdownComponentV2, EvAccordionComponent, ReactiveFormsModule, TimePeriodComponent],
    templateUrl: "./commercial-filter.component.html",
    styleUrl: "./commercial-filter.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommercialFilterComponent {
    private readonly rightSidebarService = inject(RightSidebarService);
    private readonly filterStore = inject(CommercialFilterStore);
    private readonly fb = inject(FormBuilder);
    private readonly mapDataService = inject(MapDataService);
    evServiceType = EVServiceType;

    form: FormGroup<FilterFormInterface>;

    reset: Subject<boolean> = new Subject();

    isdirty = this.filterStore.isDirty;
    constructor() {
        this.init();
    }

    protected commercialSubTypeResource = rxResource({
        stream: () => this.commercialSubTypeList$,
    });
    protected agriculturalSubTypeResource = rxResource({
        stream: () => this.agriculturalSubTypeList$,
    });
    protected otherEstateTypesResource = rxResource({
        stream: () => this.otherEstateTypesList$,
    });
    private readonly commercialSubTypeList$: Observable<CheckBoxAlternative[]> = this.mapDataService.estateTypesAndSubtypes$.pipe(
        map((x) =>
            x
                .filter((x) => x.estateType === "Næring")
                .map((y) => {
                    return { value: y.estateSubType, label: y.descriptionStripped };
                }),
        ),
    );
    private readonly agriculturalSubTypeList$: Observable<CheckBoxAlternative[]> = this.mapDataService.estateTypesAndSubtypes$.pipe(
        map((x) =>
            x
                .filter((x) => x.estateType === "Landbruk")
                .map((y) => {
                    return { value: y.estateSubType, label: y.descriptionStripped };
                }),
        ),
    );
    private readonly otherEstateTypesList$: Observable<CheckBoxAlternative[]> = this.mapDataService.estateTypes$.pipe(
        map((x) =>
            x
                .filter((x) => !this.excludeFromOthers.includes(x.estateType))
                .map((y) => {
                    return { value: y.estateType, label: y.description };
                })
                .sort((a, b) => {
                    return a.label.localeCompare(b.label);
                }),
        ),
    );
    private readonly excludeFromOthers: number[] = [
        EVEstateType.Landbruk,
        EVEstateType.Næring,
        EVEstateType.Bolig,
        EVEstateType.Boligtomt,
        EVEstateType.Hyttetomt,
        EVEstateType.Borettslag,
        EVEstateType.DeltBolig,
        EVEstateType.Enebolig,
        EVEstateType.Fritidsbolig,
        EVEstateType.Garasje,
        EVEstateType.Hus,
        EVEstateType.Hybel,
        EVEstateType.Leilighet,
        EVEstateType.Rekkehus,
        EVEstateType.Tomannsbolig,
        EVEstateType.Hytte,
        EVEstateType.Annet,
        EVEstateType.Ubebygd,
    ];

    closeFilter() {
        this.rightSidebarService.toggleSidebarVisibility(RightSidebarOptions.Closed);
    }

    resetFilter() {
        this.reset.next(true);
        this.filterStore.resetFilter();
    }

    private init() {
        this.form = this.fb.group({
            minAskingPrice: new FormControl<number>(0),
            maxAskingPrice: new FormControl<number>(0),
            minBuildYear: new FormControl<number>(0),
            maxBuildYear: new FormControl<number>(0),
            minUsableArea: new FormControl<number>(0),
            maxUsableArea: new FormControl<number>(0),
            maxGrossArea: new FormControl<number>(0),
            minGrossArea: new FormControl<number>(0),
            commercialSubTypes: new FormControl<number[] | null>(null),
            agriculturalSubTypes: new FormControl<number[] | null>(null),
            otherEstateTypes: new FormControl<number[] | null>(null),
            since: new FormControl<Date | null>(null),
            toDate: new FormControl<Date | null>(null),
        });
        this.setFilterStoreWhenFormUpdatesSubscription();

        this.setValueWhenFilterStoreUpdatesSubscription();
    }

    private setFilterStoreWhenFormUpdatesSubscription() {
        this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
            this.filterStore.setFilter(value);
        });
    }

    private setValueWhenFilterStoreUpdatesSubscription() {
        toObservable(this.filterStore.filterValue)
            .pipe(takeUntilDestroyed(), deepDistinctUntilChanged())
            .subscribe((filter) => {
                this.form.patchValue(filter);
            });
    }
}
export interface FilterFormInterface {
    minAskingPrice: FormControl<number>;
    maxAskingPrice: FormControl<number>;
    minBuildYear: FormControl<number>;
    maxBuildYear: FormControl<number>;
    minUsableArea: FormControl<number>;
    maxUsableArea: FormControl<number>;
    commercialSubTypes: FormControl<number[] | null>;
    agriculturalSubTypes: FormControl<number[] | null>;
    otherEstateTypes: FormControl<number[] | null>;
    since: FormControl<Date | null>;
    toDate: FormControl<Date | null>;
    maxGrossArea: FormControl<number>;
    minGrossArea: FormControl<number>;
}
